
import Vue from 'vue';
import { annotationSourceReference } from '@/utils/annotationSource';
import { mapActions, mapGetters, mapState } from 'vuex';
import { db, storage } from '@/firebase';
import { NODE_RUN_RESULT_SELECTION_MODE } from '@/views/Runnits/constants';
import RunnitDynamicFieldLabelRow from '@/views/Runnits/RunnitSettings/RunnitDynamicFieldLabelRow.vue';
import {
	RunnitMask,
	RunnitNodeRunInputValue,
	RunnitNodeRunResult,
	RunnitUpload,
	UploadFile,
	IMAGE_UPLOAD_MODE
} from '@run-diffusion/shared';
import _cloneDeep from 'lodash/cloneDeep';
import ImageInputSelectedImageContainer from '@/views/Runnits/RunnitSettings/ImageInput/ImageInputSelectedImageContainer.vue';
import { IMAGE_GALLERY_DIALOG_NAV, ImageGalleryDialogNav } from '@/components/ImageGallery/utils';
import { RunnitDynamicFieldMixin } from '@/views/Runnits/RunnitSettings/mixins/RunnitDynamicFieldMixin';
import { RunnitBulkActionsMixin } from '@/mixins/RunnitBulkActionsMixin';
import { RunnitsImageSelectMixin } from '@/mixins/RunnitsImageSelectMixin';
import InpaintingEditor from '../InpaintingEditor/InpaintingEditor.vue';
import VisualPromptEditor from '../VisualPromptEditor/VisualPromptEditor.vue';
import Pose3DEditor from '../Pose3DEditor/Pose3DEditor.vue';
import BigNumber from 'bignumber.js';

export default Vue.extend({
	name: 'ImageInput',
	mixins: [
		RunnitDynamicFieldMixin,
		RunnitBulkActionsMixin,
		RunnitsImageSelectMixin,
	],
	props: {
		field: { type: Object, required: true },
		value: { type: Object, default: null },
		nodeId: { type: String, default: null },
		adminOnly: { type: Boolean, default: false },
	},
	data () {


		return {
			IMAGE_UPLOAD_MODE,
			IMAGE_GALLERY_DIALOG_NAV,

			isUploading: false,
			isBeingDraggedOver: false,

			imageDetails: null,
			annotateSessionImgSnapshot: null as RunnitNodeRunInputValue | null,
		};
	},
	destroyed () {
		this.exitSelectionMode(this.selectionStateTriggerId);
	},
	computed: {
		...mapState([
			'runnitState',
			'user',
		]),
		...mapGetters([
			'getInpaintingEditorState',
		]),
		getVisualPromptEditorState () {
			const states = this.runnitState.visualPromptEditorStates;
			return new Proxy(states, {
				get: (target, prop: string) => {
					return target[prop] || {
						open: false,
						saving: false,
						backgroundImageUrl: null,
						replaceImageTarget: null,
					};
				},
			});
		},
		getPose3DEditorState () {
			const states = this.runnitState.pose3DEditorStates;
			return new Proxy(states, {
				get: (target, prop: string) => {
					return target[prop] || {
						open: false,
						saving: false,
						assetFile: null,
						assetName: null,
					};
				},
			});
		},
		selectionStateTriggerId () {
			return `image-input-${this.field.uuid}`;
		},
		hasInpainting () {
			return !!this._get(this.field, 'display.hasInpainting');
		},
		hasVisualPrompt () {
			return !!this._get(this.field, 'display.hasVisualPrompt');
		},
		hasPose3D () {
			return !!this._get(this.field, 'display.hasPose3D');
		},
		inpaintingRequired () {
			return this.hasInpainting && this.field.required && !!this._get(this.field, 'display.inpaintingRequired');
		},
		hasStrength () {
			return !!this._get(this.field, 'display.hasStrength');
		},
		imageValue () {
			let val = this.value
			if (this.hasInpainting || this.hasStrength) {
				val = this._get(this.value, 'image') || null;
			}
			return val;
		},
		inpaintingValue () {
			if (this.hasInpainting) {
				return this._get(this.value, 'inpainting') || { mask: null };
			}
			return null;
		},
		strengthValue () {
			if (this.hasStrength) {
				return this._get(this.value, 'strength') || 0.8;
			}
			return null;
		},
		originalImageValue () {
			if (this.hasInpainting || this.hasStrength) {
				return this._get(this.value, 'image.originalImage') || null;
			}
			return this._get(this.value, 'originalImage') || null;
		},
		annotateSessionOpenForField (): boolean {
			return !!this.getVisualPromptEditorState[this.field.uuid]?.open;
		},
		selectionStateForTrigger () {
			return this.getSelectionStateByTrigger(this.selectionStateTriggerId);
		},
	},
	watch: {
		annotateSessionOpenForField (isOpen: boolean, wasOpen: boolean) {
			if (isOpen && !wasOpen) {
				this.annotateSessionImgSnapshot = _cloneDeep(this.value);
			}
		},
		value: {
			immediate: true,
			async handler (newVal: RunnitNodeRunInputValue, oldVal: RunnitNodeRunInputValue) {
				if (this.hasInpainting) {
					const mask = this._get(newVal, 'inpainting.mask') || {}
					await this.setMaskURL(this._get(mask, 'id') || null);
				} else {
					this.setMaskURL(null);
				}
			},
		},
		selectionStateForTrigger: {
			immediate: false,
			handler (newVal, oldVal) {
				this.commitSingleSelectionStateValue(
					newVal,
					oldVal,
					this.field.uuid,
					this._get(this.imageValue, 'nodeRunResult.uuid') || null,
					this._get(this.imageValue, 'upload.id') || null,
					() => this.onInput(null, null, null),
					(value) => this.onInput(
						this.withOriginalImage(value, this.extractImageSource(value)),
						this.inpaintingValue,
						this.strengthValue,
					),
				);
			},
		},
	},
	methods: {
		...mapActions([
			'updateRunnitState',
			'updateInpaintingEditorState',
			'updateVisualPromptEditorState',
			'updateSnackbar',
		]),
		onInput (image: RunnitNodeRunInputValue, inpainting: RunnitNodeRunInputValue, strength: number) {
			if (this.hasInpainting || this.hasStrength) {
				if (image || inpainting || strength) {
					this.$emit('input', {
						image,
						inpainting,
						strength,
					});
				} else {
					this.$emit('input', null);
				}
			} else {
				this.$emit('input', image);
			}
		},
		extractImageSource (image: RunnitNodeRunInputValue | null): RunnitNodeRunInputValue | null {
			if (!image) return null;
			if (image.nodeRunResult) {
				return { nodeRunResult: image.nodeRunResult };
			}
			if (image.upload) {
				return { upload: image.upload };
			}
			return null;
		},
		withOriginalImage (image: RunnitNodeRunInputValue | null, originalImage: RunnitNodeRunInputValue | null): RunnitNodeRunInputValue | null {
			if (!image) return null;
			const nextOriginalImage = originalImage || this.extractImageSource(image);
			return {
				...image,
				...(nextOriginalImage && { originalImage: nextOriginalImage }),
			};
		},
		clearValueAndStartSelectSelectionMode () {
			this.onInput(null, null, null);
			this.clearSelectionsFromState(this.selectionStateTriggerId);
			this.enterSelectionMode(NODE_RUN_RESULT_SELECTION_MODE.SINGLE, this.selectionStateTriggerId, this.field, ['IMG']);
		},
		onStrengthChange (strength) {
			this.onInput(this.imageValue, this.inpaintingValue, strength);
		},
		startLibrarySelectionMode (imageGalleryDialogInitialNav: ImageGalleryDialogNav) {
			this.updateRunnitState({
				imageGalleryDialogOpen: true,
				imageGalleryDialogInitialNav: imageGalleryDialogInitialNav,
			});
			this.enterSelectionMode(NODE_RUN_RESULT_SELECTION_MODE.SINGLE, this.selectionStateTriggerId, this.field, ['IMG']);
		},
		onToggleImageSelectionMode () {
			if (this.currentSelectionState.triggeringId) {
				this.exitSelectionMode(this.currentSelectionState.triggeringId);
			} else {
				this.enterSelectionMode(NODE_RUN_RESULT_SELECTION_MODE.SINGLE, this.selectionStateTriggerId, this.field, ['IMG']);
			}
		},
		async onInpaintingMaskChange (mask) {
			this.onInput(this.imageValue, { ...this.inpaintingValue, mask }, this.strengthValue);
			await this.setMaskURL(this._get(mask, 'id') || null);
		},
		onVisualPromptChange (newImageValue) {
			const fallbackOriginal = this.extractImageSource(this.imageValue);
			const originalImage = this.originalImageValue || fallbackOriginal;
			const imageWithOriginal = this.withOriginalImage(newImageValue, originalImage);
			// The composited image replaces the current image but retains original source
			this.onInput(imageWithOriginal, this.inpaintingValue, this.strengthValue);
			this.annotateSessionImgSnapshot = null;
		},
		onVisualPromptAnnotateCancel () {
			if (this.annotateSessionImgSnapshot == null) return;
			const snap = _cloneDeep(this.annotateSessionImgSnapshot);
			if (this.hasInpainting || this.hasStrength) {
				this.onInput(this._get(snap, 'image') || snap, this._get(snap, 'inpainting'), this._get(snap, 'strength'));
			} else {
				this.onInput(snap);
			}
			this.annotateSessionImgSnapshot = null;
		},
		onVisualPromptAnnotateSubmitDismissed () {
			this.annotateSessionImgSnapshot = null;
		},
		async onVisualPromptResetToOriginal () {
			if (!this.originalImageValue) return;
			const original = this.originalImageValue;
			const baseSource = annotationSourceReference(original.upload || original.nodeRunResult, original.upload ? 'upload' : 'nodeRunResult');
			const src = this.extractImageSource(this.originalImageValue as RunnitNodeRunInputValue);
			const imageWithOriginal = this.withOriginalImage(
				src,
				this.originalImageValue as RunnitNodeRunInputValue,
			);
			this.onInput(imageWithOriginal, this.inpaintingValue, this.strengthValue);
			if (!baseSource) {
				// A legacy derivative can reset to an excluded 3D/layered original.
				this.updateVisualPromptEditorState({ fieldUuid: this.field.uuid, editorState: { open: false, backgroundImageUrl: null, baseSource: null } });
				return;
			}
			const { imageUrl } = await this.fetchSrc(
				original.upload || original.nodeRunResult,
				original.upload ? 'upload' : 'nodeRunResult'
			);
			this.updateVisualPromptEditorState({
				fieldUuid: this.field.uuid,
				editorState: {
					open: true,
					backgroundImageUrl: imageUrl,
					baseSource,
				},
			});
		},
		onPose3DCapture (newImageValue) {
			// The rendered Pose3D screenshot replaces the original image value.
			this.onInput(newImageValue, this.inpaintingValue, this.strengthValue);
		},
		async setMaskURL (maskId) {
			if (maskId) {
				const maskRef = db.doc(`runnitMasks/${maskId}`);
				const mask: RunnitMask = (await maskRef.get()).data() as RunnitMask;

				const maskImageRef = storage.ref(`runnitMasks/users/${mask.userId}/masks/${mask.name}`);

				let maskURL = null;
				try {
					// Exists
					maskURL = await maskImageRef.getDownloadURL();
				} catch (err) {
					// doesn't exist
				}
				this.updateInpaintingEditorState({
					fieldUuid: this.field.uuid,
					editorState: {
						maskUrl: maskURL,
					},
				});
			} else {
				this.updateInpaintingEditorState({
					fieldUuid: this.field.uuid,
					editorState: {
						maskUrl: null,
					},
				});
			}
		},
		validateImageAspectRatio (v: RunnitNodeRunInputValue) {
			if (!v) return true; // no value so can't check its ratio
			const imageDetails: {
				width: number,
				height: number,
			} = v.nodeRunResult ? {
				width: v.nodeRunResult.file.width,
				height: v.nodeRunResult.file.height,
			} : v.upload ? {
				width: v.upload.width,
				height: v.upload.height,
			} : null;
			if (!imageDetails) return true; // we don't have any information on the image yet

			let { maxAspectRatio } = this.field.display || {};
			maxAspectRatio = maxAspectRatio || 3;

			const aspectRatio: BigNumber = new BigNumber(imageDetails.width).div(imageDetails.height);

			if (aspectRatio.isGreaterThan(maxAspectRatio)) {
				return `Image aspect ratio (${aspectRatio.toFixed(2)}:1) is too wide. Maximum allowed is ${maxAspectRatio}:1`;
			}
			if (aspectRatio.isLessThan(new BigNumber(maxAspectRatio).pow(-1))) {
				return `Image aspect ratio (1:${aspectRatio.pow(-1).toFixed(2)}) is too tall. Maximum allowed is 1:${maxAspectRatio}`;
			}

			return true;
		},
		validateImageMinDimension (v: RunnitNodeRunInputValue) {
			if (!v) return true; // no value so can't check its dimensions
			const imageDetails: {
				width: number,
				height: number,
			} = v.nodeRunResult ? {
				width: v.nodeRunResult.file.width,
				height: v.nodeRunResult.file.height,
			} : v.upload ? {
				width: v.upload.width,
				height: v.upload.height,
			} : null;
			if (!imageDetails) return true; // we don't have any information on the image yet

			const { minDimension } = this.field.display || {};
			if (!minDimension) return true; // no minimum dimension requirement

			if (imageDetails.width < minDimension) {
				return `Image width (${imageDetails.width}px) is too small. Minimum required is ${minDimension}px`;
			}
			if (imageDetails.height < minDimension) {
				return `Image height (${imageDetails.height}px) is too small. Minimum required is ${minDimension}px`;
			}

			return true;
		},
		handleImageUpload (upload: any) {
			// Handle different types of uploads
			if (upload.nodeRunResult) {
				const image = { nodeRunResult: upload.nodeRunResult };
				const imageWithOriginal = this.withOriginalImage(image, this.extractImageSource(image));
				this.onInput(imageWithOriginal, this.inpaintingValue, this.strengthValue);
			} else if (upload.upload) {
				const image = { upload: upload.upload };
				const imageWithOriginal = this.withOriginalImage(image, this.extractImageSource(image));
				this.onInput(imageWithOriginal, this.inpaintingValue, this.strengthValue);
			} else {
				const image = { upload };
				const imageWithOriginal = this.withOriginalImage(image, this.extractImageSource(image));
				this.onInput(imageWithOriginal, this.inpaintingValue, this.strengthValue);
			}
		},
	},
	components: {
		ImageInputSelectedImageContainer,
		RunnitDynamicFieldLabelRow,
		InpaintingEditor,
		VisualPromptEditor,
		Pose3DEditor,
	},
});
